import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule, DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { AppFilterPipeModule } from 'app/_filters/app.filter-pipe.module';
import { BaseRequestService } from 'app/_services/base.service';
import { CommonService } from 'app/_services/common.services';
import { LoaderService } from 'app/_services/loader.service';
import { ModalService } from 'app/_services/modal.service';
import { MyToastrService } from 'app/_services/toastr.service';
import { UserService } from 'app/core/user/user.service';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { Notification } from 'app/layout/common/notifications/notifications.types';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        NgClass,
        NgTemplateOutlet,
        RouterLink,
        DatePipe,
        AppFilterPipeModule,
    ],
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel')
    private _notificationsPanel: TemplateRef<any>;
    intervalId: any;
    notifications: any[] = [];
    unreadCount: number = 0;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    user: any;
    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        public baseService: BaseRequestService,
        public modalService: ModalService,
        public toast: MyToastrService,
        private loaderService: LoaderService,
        public _cs: CommonService,
        private router: Router,
        private userservice: UserService,
    ) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // 1 minute interval
        setTimeout(() => {
            try {
                this.userservice.userd$
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe((user: any) => {
                        this.user = JSON.parse(JSON.stringify(user));
                        this.getNotifications(); // Initial call
                        // Call the API every minute (60,000 ms = 1 minute)
                        this.intervalId = setInterval(() => {
                            this.getNotifications();
                        }, 300000);
                    });
            } catch (error) {
                if (!this.user) {
                    this.user = JSON.parse(localStorage.getItem('user'))
                }

            }
        }, 1000);
    }

    getNotifications(): void {
        this.unreadCount = 0;
        this.notifications = [];
        const todayMidnightUTC = new Date();
        todayMidnightUTC.setUTCHours(0, 0, 0, 0); // Set time to 00:00:00 UTC
        todayMidnightUTC.setUTCHours(todayMidnightUTC.getUTCHours() - 5);
        todayMidnightUTC.setUTCMinutes(todayMidnightUTC.getUTCMinutes() - 30);
        const formattedDate = todayMidnightUTC.toISOString();
        this.loaderService.display(true, 'Loading the Notifications');
        let condition = {
            condition: `c.status ='Lock' and c.datetime >= '${formattedDate}'`,
            skip: 0,
            limit: 1000,
        };
        this.baseService
            .doRequest('/interlock', 'get', null, condition)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(
                (res: any) => {
                    if (res && res.data.length > 0) {
                        this.notifications = res.data
                        this.unreadCount = this.notifications.length;
                        this._changeDetectorRef.detectChanges();
                    }
                },
                (error: any) => {
                    this.toast.sToast('error', `${error.message}`, `Error Code : ${error.status}`);
                    console.log('User error', error);
                }
            )
        this.loaderService.display(false);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }
    onNotificationClick(notification) {
        this.baseService.doRequest('/ifms/scheduler', 'post', { data: notification }).subscribe(
            (res: any) => {
                if (res.status === true) {
                    this.loaderService.display(false);
                    this.router.navigateByUrl('/scheduler');
                    this.closePanel();
                    setTimeout(() => {
                        this.getNotifications();
                    }, 1000);
                    this._changeDetectorRef.detectChanges();
                }
                else {
                    this.loaderService.display(false);
                    this.toast.sToast('error', `${res.data}`);
                }
            },
            (error: any) => {
                this.loaderService.display(false);
                this.toast.sToast('error', 'Something Went Wrong', `Error Code ${error.status}`);
            }
        )
        // this.router.navigateByUrl('/scheduler');
        // this.closePanel();

    }
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void {
        // Return if the notifications panel or its origin is not defined
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }
        this.getNotifications();
        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(
            new TemplatePortal(this._notificationsPanel, this._viewContainerRef)
        );
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void {
        this._overlayRef.detach();
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void {
        // Mark all as read
        this._notificationsService.markAllAsRead().subscribe();
    }

    /**
     * Toggle read status of the given notification
     */
    toggleRead(notification: Notification): void {
        // Toggle the read status
        notification.read = !notification.read;

        // Update the notification
        this._notificationsService
            .update(notification.id, notification)
            .subscribe();
    }

    /**
     * Delete the given notification
     */
    delete(notification: Notification): void {
        // Delete the notification
        this._notificationsService.delete(notification.id).subscribe();
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(
                    this._notificationsOrigin._elementRef.nativeElement
                )
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom',
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top',
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom',
                    },
                ]),
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }

    /**
     * Calculate the unread count
     *
     * @private
     */
    private _calculateUnreadCount(): void {
        let count = 0;

        if (this.notifications && this.notifications.length) {
            count = this.notifications.filter(
                (notification) => !notification.read
            ).length;
        }

        this.unreadCount = count;
    }
}
